import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Lock, Loader2 } from 'lucide-react';

const CheckInPage = () => {
  const { confirmationCode } = useParams();
  
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [error, setError] = useState('');

  const handleCheckIn = async (e) => {
    e.preventDefault();
    
    if (!pin || pin.length !== 6) {
      setError('Please enter a 6-digit PIN');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('pin', pin);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/check-in/${confirmationCode}`,
        {
          method: 'POST',
          body: formData
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'Invalid PIN');
        setLoading(false);
        return;
      }

      // Success - show success state
      setCheckedIn(true);
      setBookingDetails(data.booking || {});
      
    } catch (err) {
      setError('Check-in failed. Please try again.');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  // SUCCESS STATE - Simple and clean
  if (checkedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex flex-col items-center justify-center p-4 relative">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          {/* Big Success Checkmark */}
          <div className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-6">
            <Check className="w-14 h-14 text-white" strokeWidth={3} />
          </div>
          
          {/* Success Message */}
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Checked In!
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Customer successfully checked in
          </p>
          
          {/* Customer Info (if available) */}
          {bookingDetails?.customerName && (
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-500 mb-1">Customer</p>
              <p className="text-xl font-bold text-gray-900">{bookingDetails.customerName}</p>
            </div>
          )}
          
          {/* Done Button */}
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold transition shadow-sm text-lg"
          >
            Done
          </button>
        </div>
        
        {/* Powered by - Outside card */}
        <div className="absolute bottom-8 text-center">
          <p className="text-sm text-gray-600">
            Powered by <span className="text-blue-600 font-semibold">BookingFlows</span>
          </p>
        </div>
      </div>
    );
  }

  // PIN ENTRY STATE
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex flex-col items-center justify-center p-4 relative">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Check In</h1>
          <p className="text-gray-600">Booking: <span className="font-mono font-bold">{confirmationCode}</span></p>
        </div>

        <form onSubmit={handleCheckIn} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Staff PIN
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit PIN"
              className="w-full text-center text-2xl font-mono h-14 tracking-widest border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
              maxLength={6}
              required
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-2 text-center">
              Ask your manager for the 6-digit check-in PIN
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 text-center">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || pin.length !== 6}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Checking In...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Check In Customer
              </>
            )}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t text-center">
          <p className="text-xs text-gray-500">
            Staff members only. Customer check-in verification.
          </p>
        </div>
      </div>
      
      {/* Powered by - Outside card */}
      <div className="absolute bottom-8 text-center">
        <p className="text-sm text-gray-600">
          Powered by <span className="text-blue-600 font-semibold">BookingFlows</span>
        </p>
      </div>
    </div>
  );
};

export default CheckInPage;
